export type OneRepMax = {
  name: string
  oneRepMax: number
}

export type WorkoutSet = {
  index: number
  type: string
  weight_kg: number
  reps: number
  distance_meters: number | null
  duration_seconds: number | null
  rpe: number | null
  rest_seconds?: number | null
  custom_metric: Record<string, unknown> | null
}

export type WorkoutExercise = {
  index: number
  title: string
  notes: string
  exercise_template_id: string
  superset_id: string | null
  sets: WorkoutSet[]
  // Enhanced muscle group data from exercise processor
  primaryMover?: MuscleGroup
  secondaryMovers?: MuscleGroup[]
  isBodyweight?: boolean
  isAssisted?: boolean
  detectionMethod?: 'override' | 'template_id' | 'pattern_matching' | 'unknown'
}

export type Workout = {
  id: string
  title: string
  description: string
  start_time: string
  end_time: string
  updated_at: string
  created_at: string
  exercises: WorkoutExercise[]
}

export type WorkoutsResponse = {
  page: number
  page_count: number
  workouts: Workout[]
}

export interface WeeklyStats {
  sessionCount: number
  // Physical sets (unique sets performed) - always shows actual sets regardless of secondary movers toggle
  totalPhysicalSets: number
  // Weighted sets (includes secondary movers if enabled) - used for individual muscle group cards
  chestSets: number
  latsSets: number
  upperBackSets: number
  rearDeltsSets: number
  lateralDeltsSets: number
  frontDeltsSets: number
  bicepsSets: number
  tricepsSets: number
  quadsSets: number
  hamstringsSets: number
  glutesSets: number
  adductorsSets: number
  calvesSets: number
  absSets: number
  forearmsSets: number
  neckSets: number
  weightData: WeightEntry[]
  // Exercise breakdown for tooltips
  exerciseBreakdown: Record<string, Array<{ name: string; sets: number; isPrimary?: boolean }>>
  // Previous week data for comparison
  previousWeek: {
    totalPhysicalSets: number
    chestSets: number
    latsSets: number
    upperBackSets: number
    rearDeltsSets: number
    lateralDeltsSets: number
    frontDeltsSets: number
    bicepsSets: number
    tricepsSets: number
    quadsSets: number
    hamstringsSets: number
    glutesSets: number
    adductorsSets: number
    calvesSets: number
    absSets: number
    forearmsSets: number
    neckSets: number
    exerciseBreakdown: Record<string, Array<{ name: string; sets: number; isPrimary?: boolean }>>
  }
}

export type WeightEntry = {
  date: string
  weight: number
}

export type MuscleGroup =
  | 'chest'
  | 'lats'
  | 'upperBack'
  | 'rearDelts'
  | 'lateralDelts'
  | 'frontDelts'
  | 'biceps'
  | 'triceps'
  | 'quads'
  | 'hamstrings'
  | 'glutes'
  | 'adductors'
  | 'calves'
  | 'abs'
  | 'forearms'
  | 'neck'

export interface OneRepMaxDataPoint {
  date: string
  oneRepMax: number
  smoothed?: number
  trend?: number // Smoothed trend value for overlay line
  bestWeight?: number // Raw best weight (highest weight lifted on this date)
  weight?: number // Actual weight lifted
  reps?: number // Reps performed
  // Enhanced support for bodyweight exercises
  maxReps?: number // Max reps achieved for bodyweight exercises
  repsTrend?: number // Smoothed reps trend for bodyweight exercises
  isBodyweight?: boolean // Flag to indicate bodyweight exercise
  isAssisted?: boolean // Flag to indicate assisted exercise
}

export interface Exercise {
  name: string
  templateId?: string
  primaryMover?: MuscleGroup
  secondaryMovers?: MuscleGroup[]
  isBodyweight?: boolean
  isAssisted?: boolean
}

export interface Set {
  weight: number
  reps: number
  notes?: string
  exerciseName: string
  date: string
  muscleGroup: MuscleGroup // Legacy field
  templateId?: string
  primaryMover?: MuscleGroup
  secondaryMovers?: MuscleGroup[]
  isBodyweight?: boolean
  isAssisted?: boolean
}

export interface CategorizedOneRepMax extends Exercise {
  chartData: OneRepMaxDataPoint[]
  currentMax: number
}

// Weight tracking types
export interface WeightData {
  date: string
  weight: number
  fatMass?: number
  muscleMass?: number
  source: 'withings' | 'manual'
}

export interface WeightDataPoint {
  date: string
  weight: number // in kg
  timestamp: number
}

export interface WeightAnalysis {
  weightData: WeightDataPoint[]
  smoothedData: number[]
  trendData: WeightDataPoint[]
  weeklyChange: number
  caloricSurplus: number
  confidenceInterval: { lower: number[]; upper: number[] }
  plateauDetected: boolean
  isLoading: boolean
  error: string | null
}

// Calendar types
export interface CalendarDay {
  date: Date
  sets: number
  muscleGroups: string[]
}

// Routine types
export interface RoutineSet {
  index: number
  type: string
  weight_kg: number | null
  reps: number | null
  distance_meters: number | null
  duration_seconds: number | null
  custom_metric: Record<string, unknown> | null
}

export interface RoutineExercise {
  index: number
  title: string
  notes: string | null
  exercise_template_id: string
  superset_id: string | null
  sets: RoutineSet[]
  rest_seconds: number
  // Enhanced muscle group data
  primaryMover?: MuscleGroup
  secondaryMovers?: MuscleGroup[]
  isBodyweight?: boolean
  isAssisted?: boolean
  detectionMethod?: 'override' | 'template_id' | 'pattern_matching' | 'unknown'
}

export interface Routine {
  id: string
  title: string
  folder_id?: number | null
  updated_at: string
  created_at: string
  exercises: RoutineExercise[]
}

export interface RoutinesResponse {
  page: number
  page_count: number
  routines: Routine[]
}

export interface RoutineFolder {
  id: number
  index: number
  title: string
  updated_at: string
  created_at: string
}

export interface RoutineFoldersResponse {
  page: number
  page_count: number
  routine_folders: RoutineFolder[]
}

export interface WeeklySplit {
  cycle_mode?: boolean
  monday?: string[] // array of routine ids
  tuesday?: string[]
  wednesday?: string[]
  thursday?: string[]
  friday?: string[]
  saturday?: string[]
  sunday?: string[]
  day8?: string[]
  day9?: string[]
  day10?: string[]
  day11?: string[]
  day12?: string[]
  day13?: string[]
  day14?: string[]
}

export interface MuscleVolumeAnalysis {
  muscleGroup: MuscleGroup
  weeklySetCount: number
  dailyBreakdown: Record<string, number>
  exercises: Array<{
    name: string
    sets: number
    days: string[]
    isPrimary?: boolean
  }>
}

// From useProfile.ts
export interface ProfileData {
  name: string
  email: string
  memberSince: string
  fitnessGoals: {
    workoutsPerWeek: string
    primaryFocus: string
    targetWeight: number
    targetWeightUnit: 'lbs' | 'kg'
  }
}

// From useSettings.ts
export type TrainingExperience = 'beginner' | 'intermediate' | 'advanced'
export type CoachPersonality = 'supportive' | 'motivational' | 'analytical' | 'casual'
export type UserGoal = 'strength' | 'hypertrophy' | 'fat_loss'

export interface AppSettings {
  theme: 'light' | 'dark'
  units: 'metric' | 'imperial'
  dataSource: 'api' | 'csv'
  ai?: {
    enabled?: boolean
    personality: CoachPersonality
    training_experience: TrainingExperience
    user_goal?: UserGoal
    model?: 'gpt-4.1' | 'o1'
  }
  algorithms?: {
    targetWorkoutsPerWeek: number | 'auto'
    bodyweightPlateauThreshold: number
    exercisePlateauThreshold: number
  }
}

// From dashboardStore.ts
export type DashboardSection = 'calendar' | 'weight' | 'routine' | 'weeklyStats' | 'muscleGroups'

export interface SidebarItem {
  id: DashboardSection
  label: string
  icon: React.ComponentType<{ className?: string }>
  description: string
}

// Default ordering
export const DEFAULT_SECTION_ORDER: DashboardSection[] = [
  'calendar',
  'weight',
  'routine',
  'weeklyStats',
  'muscleGroups'
]

// From useWorkoutData.ts
export interface ExtendedCalendarDay {
  date: Date
  sets: number
  muscleGroups: string[]
  routineNames: string[]
  routineColors: string[]
  workoutIds: string[]
}

export type MuscleGroupDataType = 'oneRepMax' | 'bestWeight'

export interface WithingsAnalysis {
  weightData: WeightDataPoint[]
  smoothedData: number[]
  trendData: WeightDataPoint[]
  weeklyChange: number
  caloricSurplus: number
  confidenceInterval: { lower: number[]; upper: number[] }
  plateauDetected: boolean
  isLoading: boolean
  error: string | null
}

// Enhanced weight analysis for the UI component
export interface WeightAnalysisUI {
  weightData: Array<{ date: string; weight: number }>
  trendData: Array<{ date: string; weight: number }>
  currentWeight: number
  weeklyTrendChange: number
  dailyCaloricBalance: number
  phase: 'bulking' | 'cutting' | 'maintenance' | 'insufficient_data'
  plateauDetected: boolean
  hasValidTrend: boolean
}

export interface WeightChartData {
  date: string
  raw?: number
  trend?: number
}

export type DataPeriod = 'all' | '3months' | '1month' | '2weeks'

// Calendar Navigation Types
export interface DateRange {
  start: Date
  end: Date
}

export interface CalendarPeriod {
  offset: number // 0 = current, -1 = 30 days ago, +1 = future
  range: DateRange
}

// Exercise Processing Types
export interface ExerciseTemplate {
  name: string
  primary_movers: MuscleGroup
  secondary_movers: MuscleGroup[]
  bodyweight: boolean
  assisted: boolean
}

export interface ProcessedExerciseData {
  templateId: string
  name: string
  primaryMover: MuscleGroup
  secondaryMovers: MuscleGroup[]
  isBodyweight: boolean
  isAssisted: boolean
  detectionMethod: 'override' | 'template_id' | 'pattern_matching' | 'unknown'
  confidence?: 'override' | 'high' | 'medium' | 'low'
}

export interface ExerciseCoverageStats {
  total: number
  templateMatches: number
  patternMatches: number
  unknown: number
  coverage: number
}

// Enhanced muscle group analysis with detection method tracking
export interface EnhancedMuscleVolumeAnalysis extends MuscleVolumeAnalysis {
  exercises: Array<{
    name: string
    sets: number
    days: string[]
    isPrimary: boolean // Made required and non-optional
    detectionMethod?: 'override' | 'template_id' | 'pattern_matching' | 'unknown'
    templateId?: string
  }>
  detectionStats: {
    overrideBased: number
    templateBased: number
    patternBased: number
    unknown: number
  }
}

// Muscle Group Override Types
export interface MuscleGroupOverride {
  primary_mover: MuscleGroup
  secondary_movers: MuscleGroup[]
}

export type MuscleGroupOverrides = Record<string, MuscleGroupOverride>

// Hevy Exercise Templates API types
export interface HevyExerciseTemplate {
  id: string
  title: string
  type: string
  primary_muscle_group: string
  secondary_muscle_groups: string[]
  is_custom: boolean
}

export interface HevyExerciseTemplatesResponse {
  page: number
  page_count: number
  exercise_templates: HevyExerciseTemplate[]
}

// Unified exercise interface for split builder
export interface UnifiedExercise {
  id: string
  name: string
  primaryMover: MuscleGroup
  secondaryMovers: MuscleGroup[]
  isCustom: boolean
  source: 'static' | 'hevy'
}
